/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable tailwindcss/no-custom-classname */
'use client';

import classNames from 'classnames';
import { FC, MouseEventHandler, ReactNode, useMemo } from 'react';
import { ApplyCtaLink } from '../../components/ApplyCta';
import {
  ApprovalOddsBadge,
  type ApprovalOddsLabels,
} from '../../components/ApprovalOddsBadge';
import Button from '../../components/Button';
import { CmsImage } from '../../components/CmsImage';
import { EligibilityTableMarker } from '../../components/Eligibility/EligibilityNote';
import {
  playedDownRowClassNames,
  PROVIDER_LOGO_CELL,
} from '../../components/Eligibility/playedDown';
import { FeLink } from '../../components/FeLink';
import { GoogleRating } from '../../components/GoogleRating';
import {
  getGoogleLink,
  getGoogleRatingContext,
  getGoogleReviewData,
} from '../../components/GoogleReview/utils';
import Tooltip from '../../components/Tooltip';
import {
  AttributesListStyleEnum,
  BaseAttribute,
  ColumnSizeToken,
  ComponentAttribute,
  SystemAttribute,
} from '../../types/attributes';
import { CmsProduct } from '../../types/cmsProduct';
import { PageType, VerticalType } from '../../types/cmsTypes';
import type { EligibilityVerdict } from '../../types/eligibility';
import { PushDataLayerFn } from '../../types/pushDataLayer';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '../../utils/analytics';
import { getApprovalOddsStatus } from '../../utils/approvalOdds';
import { sortProductsByApprovalOdds } from '../../utils/approvalOddsSorting';
import {
  CmsClientContextValue,
  useCmsClientContext,
} from '../../utils/cmsPageContext';
import {
  formatAttributeItemValue,
  formatNumberWithSeparator,
} from '../../utils/helpers';
import { calculateRewardValue } from '../../utils/helpers/calculateRewardValue';
import { currencyFormat } from '../../utils/helpers/formatters';
import { BrandId } from '../../utils/override';
import { getHighersRewardExclusiveOffersAndWelcomeOffersGitsFromProduct } from '../../utils/rewards';
import { LoanSearchParams } from '../../utils/searchParams/loanSearchParams';
import { encodeHashParams } from '../../utils/searchParams/urlHelpers';
import { sessionAttributionTracker } from '../../utils/sessionAttributionTracking/sessionAttributionTracker';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { TranslationUnWrapper } from '../../utils/translations/staticText';
import { tr } from '../../utils/translations/tr';
import { applyProductFilter } from '../SortAndFilterPanel/applyProductFilter';
import { applyProductSort } from '../SortAndFilterPanel/applyProductSort';
import { FilterEmptyState } from '../SortAndFilterPanel/FilterEmptyState';
import { useProductEligibilityState } from '../SortAndFilterPanel/ProductEligibilityProvider';
import { useProductFilterState } from '../SortAndFilterPanel/ProductFilterProvider';
import type { FilterableProduct } from '../SortAndFilterPanel/productFilterTypes';
import { useActiveProductSort } from '../SortAndFilterPanel/ProductSortProvider';
import { isPlayedDown } from '../SortAndFilterPanel/resolveEligibility';
import {
  ComparisonTableBuilderColumn,
  ComparisonTableBuilderProps,
} from './ComparisonBuilderInterface';
import { ComparisonTableBuilder } from './ComparisonTableBuilder';
import {
  ColumnType,
  GetDefaultColumnTypes,
  hasRequiredColumnValues,
  GetSize,
  GetColumnSizes,
  GetColumnSizeByKey,
  getCardTitle,
} from './ComparisonTableHelpers';
import { NA } from './components/Na';
import {
  ROW_LABEL_RAIL_CLASSES,
  RowLabelRail,
} from './components/RowLabelRail';
import {
  ATTRIBUTE_COLUMNS_MAX,
  HOT_REWARD_TRACKING_TITLE,
  REWARD_CELL_LINK_CLASSES,
  SingSaverRewardsSectionId,
} from './constants';
import { TRANSLATIONS } from './i18n';
import {
  ApprovalOddsData,
  ComparisonTableProps,
  ComparisonTableProduct,
  OnRewardColumnClickHandler,
  BaseColumnComparisonTable,
  PersonalLoanOrder,
} from './types';
import { getHotRewardDisplayText } from './utils';

type ComparisonTableColumn =
  ComparisonTableBuilderColumn<ComparisonTableProduct>;

type AttributeListRecord = Partial<
  Record<
    ColumnType,
    {
      header: string;
      size: ColumnSizeToken | undefined;
      style: AttributesListStyleEnum | undefined;
    }
  >
>;

type TranslationsType = typeof TRANSLATIONS;
type StaticText = ReturnType<
  typeof getClientTranslations<TranslationUnWrapper<TranslationsType>>
>;

export const ComparisonTable: FC<ComparisonTableProps> = (props) => {
  const {
    tableConfig: {
      attributes: comparisonTableAttributes,
      baseColumnOrder: baseCTColumns,
      personal_loan_order,
      maxVisibleProducts: showMoreCount,
    },
    products,
    calculateError = false,
    vertical: sourceVertical,
    loanSearchParams,
    slug,
    activeAttribute,
    onRewardColumnClick,
    onRowClick,
    isRowSelected,
    pushDataLayer,
    approvalOddsData,
    sortByApprovalOdds,
    onApprovalOddsUnlockClick,
    onApprovalOddsClick,
    footer,
  } = props;
  const vertical = sourceVertical ?? 'CREDIT_CARD';
  const context = useCmsClientContext();
  const { brand, lang } = context;
  const { pageType } = props;
  const staticText = getClientTranslations(TRANSLATIONS, { brand, lang });
  const baseColumns = baseCTColumns ?? [];
  const attributes = useMemo(() => {
    if (activeAttribute.length === 0) {
      return comparisonTableAttributes;
    }
    return comparisonTableAttributes?.filter((attr) =>
      activeAttribute.includes(attr.attribute?.name as SystemAttribute),
    );
  }, [comparisonTableAttributes, activeAttribute]);

  // Marks the rows, and the Apply column's note. Deliberately not an input to
  // `productsToRender` below: eligibility removes no row and reorders none
  // (AC 7). Read here rather than lower down because the columns carry the note.
  const { verdictFor } = useProductEligibilityState();

  const columnsSource = useMemo(
    () =>
      getColumnSourceByVertical(
        vertical,
        staticText,
        pageType,
        slug ?? '',
        // ToDo: use TableConfig instead of personal_loan_order
        personal_loan_order,
        comparisonTableAttributes,
        pushDataLayer,
        context,
        approvalOddsData,
        onApprovalOddsUnlockClick,
        onApprovalOddsClick,
        verdictFor,
      ),
    [
      staticText,
      vertical,
      pageType,
      slug,
      personal_loan_order,
      comparisonTableAttributes,
      context,
      approvalOddsData,
      onApprovalOddsUnlockClick,
      onApprovalOddsClick,
      verdictFor,
    ],
  );

  const attributeList = useMemo(
    () => columnsSource.GetAttributeList(attributes ?? [], products ?? []),
    [attributes, products, columnsSource, baseColumns],
  );

  // From the context rather than a prop: a caller inside the provider could
  // forget to pass it, which is what stopped the product cards sorting on every
  // vertical except personal loans.
  const productSortOption = useActiveProductSort();
  const { categories, selection } = useProductFilterState();

  const defaultOrder = useMemo(() => {
    let filteredProducts: ComparisonTableProduct[];

    if (vertical !== 'PERSONAL_LOANS') {
      filteredProducts = products ?? [];
    } else {
      const defaultRequiredColumns = GetDefaultColumnTypes(
        context.brand,
        vertical,
      );

      filteredProducts = (products ?? []).filter(
        (product) =>
          calculateError ||
          hasRequiredColumnValues(product, defaultRequiredColumns),
      );
    }

    return sortProductsByApprovalOdds(
      filteredProducts,
      (product) => product.title,
      approvalOddsData,
      sortByApprovalOdds,
    );
  }, [
    products,
    attributeList,
    vertical,
    calculateError,
    context.brand,
    approvalOddsData,
    sortByApprovalOdds,
  ]);

  // The user's choice is applied last so it wins over the approval-odds default,
  // and always from `defaultOrder` rather than a previous sort, so returning to
  // the default option restores the original order exactly.
  //
  // Sorting the data rather than the rendered rows is also what keeps the Show
  // more cut in step: that cut is positional — the `hidden` class is keyed on a
  // row's index in this list — so the visible top-N is the sorted top-N.
  // Filtered before ordering (R6 / R7.1), and for the same reason the sort acts
  // on the data rather than the rendered rows: the Show more cut is positional,
  // so the visible top-N has to be the top-N of what survives the filters.
  const productsToRender = useMemo(() => {
    const kept = applyProductFilter(defaultOrder, categories, selection);
    return applyProductSort(
      kept,
      productSortOption,
      (product) => product.attributes,
      undefined,
      // Credit card's gift-value measure ranks on the card's own exclusive
      // offers rather than on an attribute.
      (product) => product,
    );
  }, [defaultOrder, productSortOption, categories, selection]);

  const ctaSizing = useMemo<ColumnSizeToken>(() => {
    const sizeOrder: Record<ColumnSizeToken, number> = {
      S: 1,
      M: 2,
      L: 3,
      FILL: 4,
    };

    return (
      productsToRender
        .map((product) => product?.override_cta_label ?? staticText.apply)
        .map((label) => getCtaColumnSize(label))
        .sort((a, b) => {
          return sizeOrder[b] - sizeOrder[a];
        })[0] ?? 'M'
    );
  }, [productsToRender, staticText]);

  const hasRowLabels = productsToRender.some((product) => !!product.row_label);

  const columnsToRender = useMemo(() => {
    const columns = columnsSource.GetColumns(
      attributeList,
      baseColumns,
      calculateError,
      onRewardColumnClick,
      loanSearchParams,
      ctaSizing,
    );
    // Prepended after each vertical's own column assembly so the rail never becomes part of the
    // editor-selectable column set (CMS-1906).
    return hasRowLabels
      ? [columnsSource.GetRowLabelColumn(), ...columns]
      : columns;
  }, [
    attributes,
    calculateError,
    columnsSource,
    onRewardColumnClick,
    loanSearchParams,
    ctaSizing,
    hasRowLabels,
  ]);

  const showAllText = getShowAllText(staticText);

  const comparisonTableBuilderProps: ComparisonTableBuilderProps<CmsProduct> = {
    data: productsToRender as CmsProduct[],
    context,
    translations: {},
    onRowClick,
    isRowSelected,
    slug,
    showMore: {
      showMoreText: showAllText[vertical] ?? tr('show_all_cards', staticText),
      showMoreCount,
      showMoreRender: (product) => {
        return (
          <CmsImage
            sizes={{ _: 38 }}
            src={product?.product_image ?? ''}
            alt={product?.product_name ?? staticText.product}
            width={38}
            height={24}
            // eslint-disable-next-line tailwindcss/no-arbitrary-value
            className="h-6 w-auto max-w-8 cursor-pointer object-contain md:max-w-[38px]"
          />
        );
      },
    },
    columns: columnsToRender,
    pushDataLayer,
    footer,
    /**
     * Tinted rather than faded, so the Apply button in each row keeps its full
     * prominence (requirement 4b).
     *
     * The tint goes on the **cells**, not the row: every `td` paints its own
     * background, so a row-level colour is covered and reads as no change at
     * all. The prototype's `tr.soft > td` rule exists for the same reason.
     * Greyscale is scoped to the first cell's logo — the row also carries
     * reward art and a Google mark, which are not the lender's identity.
     */
    getRowClassNames: (product) =>
      playedDownRowClassNames(verdictFor(product as FilterableProduct)),
  };

  // R8: a table with no rows left says so, rather than rendering its header
  // over nothing. Silent unless a filter is active, so an empty catalogue is
  // not misattributed to the reader's choices.
  if (productsToRender.length === 0) return <FilterEmptyState />;

  return <ComparisonTableBuilder {...comparisonTableBuilderProps} />;
};

const getLabel = (
  products: ComparisonTableProduct[] | undefined,
  name: string,
  defaultLabel: string,
): string => {
  if (!products) {
    return defaultLabel;
  }

  let attributeName: string | undefined = '';
  for (const product of products) {
    if (!product.attributes_from_meta?.length) {
      continue;
    }

    attributeName = product.attributes_from_meta?.find(
      (attr) => attr?.attribute?.name === name,
    )?.label;

    if (attributeName) {
      break;
    }
  }

  return attributeName || defaultLabel;
};

const getAttributeName = (attribute: BaseAttribute): string => {
  switch (attribute?.__typename) {
    case 'ComponentAttributesAttrRichTextEditor':
    case 'ComponentAttributesAttrNumberRange':
    case 'ComponentAttributesAttrNumber':
    case 'ComponentAttributesAttrBoolean':
    case 'ComponentAttributesAttrPlainText':
    case 'ComponentAttributesAttrKeyValue':
    case 'ComponentAttributesAttrJson': {
      return attribute!.name?.name ?? '';
    }
  }
  return attribute?.__typename ?? '';
};

/**
 * Href twin of `useProductBlockAsideModal().openModal` — keep the hash keys in step;
 * the vertical lets the drawer resolve the product the way the block rendered it.
 */
export const createGoToLink = (
  urlAlias: string,
  idScrollTo: string,
  vertical?: VerticalType,
) => {
  const hashState: Record<string, string> = {
    modalProductUrlAlias: urlAlias,
    modalProductIdScrollTo: idScrollTo,
    ...(vertical ? { modalProductVertical: vertical } : {}),
  };
  return encodeHashParams(hashState);
};

const getShowAllText = (
  staticText: StaticText,
  // Product verticals only — blog-editorial verticals (CMS-1908) never drive a product
  // ComparisonTable, so they legitimately have no "show all products" label.
): Partial<Record<VerticalType, string>> => ({
  BANKING: tr('show_all_banking_products', staticText),
  INVESTMENTS: tr('show_all_investment_products', staticText),
  PERSONAL_LOANS: tr('show_all_loans', staticText),
  CREDIT_CARD: tr('show_all_cards', staticText),
  TRAVEL_INSURANCE: tr('show_all_travel_insurance_products', staticText),
  CAR_INSURANCE: tr('show_all_car_insurance_products', staticText),
  INSURANCE: tr('show_all_cards', staticText),
  TELCO: tr('show_all_cards', staticText),
  PERSONAL_FINANCE: tr('show_all_cards', staticText),
});

/** Rendered in a reward cell that has nothing to show; also what a click on it reports. */
const EMPTY_CELL_TEXT = '-';

/**
 * Where the composite Interest rate column (AIR over EIR) renders: its copy is fixed English and EIR
 * is an SG figure. Total on purpose, so a new market does not compile without an answer (CMS-2012).
 */
const INTEREST_RATE_COLUMN_BY_BRAND: Record<BrandId, boolean> = {
  singsaver: true,
  moneyhero: false,
  money101: false,
  moneymax: false,
};

class BaseColumnSource {
  protected readonly brand: BrandId;
  protected readonly lang: string;
  protected readonly langPrefix: string;

  protected readonly defaultBaseColumns: BaseColumnComparisonTable[] = [
    {
      column: {
        keyColumn: 'BANK_LOGO',
        nameColumn: getCardTitle(this.vertical, this.staticText),
      },
      columnSizing: undefined,
      style: undefined,
    },

    {
      column: {
        keyColumn: 'PRODUCT_NAME',
        nameColumn: '',
      },
      columnSizing: undefined,
      style: undefined,
    },
    {
      column: {
        keyColumn: 'APPLY_NOW_BUTTON',
        nameColumn: '',
      },
      columnSizing: undefined,
      style: undefined,
    },
    {
      column: {
        keyColumn: 'HOT_REWARD',
        nameColumn: '',
      },
      columnSizing: undefined,
      style: undefined,
    },
    {
      column: {
        keyColumn: 'REWARD_VALUE',
        nameColumn: '',
      },
      columnSizing: undefined,
      style: undefined,
    },
  ];

  constructor(
    protected readonly staticText: StaticText,
    protected readonly vertical: VerticalType,
    protected readonly pageType: PageType | undefined,
    protected readonly slug: string,
    protected readonly pushDataLayer: PushDataLayerFn,
    context: CmsClientContextValue,
    protected readonly approvalOddsData?: ApprovalOddsData,
    protected readonly onApprovalOddsUnlockClick?: () => void,
    protected readonly onApprovalOddsClick?: () => void,
    /**
     * One loan's eligibility outcome (CMS-1905). A function rather than a map
     * so the columns rebuild only when the reader's details change, which is
     * exactly when the outcomes do.
     */
    protected readonly verdictFor?: (
      product: FilterableProduct,
    ) => EligibilityVerdict,
  ) {
    this.brand = context.brand;
    this.lang = context.lang;
    this.langPrefix = context.langPrefix;
  }

  public GetAttributeList(
    attributes: ComponentAttribute[],
    _: ComparisonTableProduct[],
  ): Partial<
    Record<
      ColumnType,
      {
        header: string;
        size: ColumnSizeToken | undefined;
        style: AttributesListStyleEnum | undefined;
      }
    >
  > {
    return (
      attributes
        ?.filter((_, index) => index < ATTRIBUTE_COLUMNS_MAX)
        .reduce<
          Partial<
            Record<
              ColumnType,
              {
                header: string;
                size: ColumnSizeToken | undefined;
                style: AttributesListStyleEnum | undefined;
              }
            >
          >
        >((prev, current) => {
          if (!current?.attribute?.name) {
            return prev;
          }

          return {
            ...prev,
            [current.attribute.name as ColumnType]: {
              header: current.label || current.attribute.label,
              size: current.columnSizing,
              style: current.style,
            },
          };
        }, {}) ?? {}
    );
  }

  /**
   * The editorial label rail (CMS-1906): a 24px column left of BANK_LOGO, styled only on rows
   * with a resolved `row_label`.
   */
  public GetRowLabelColumn(): ComparisonTableColumn {
    return {
      header: '',
      field: 'row_label',
      style: undefined,
      pinned: true,
      keepsOwnBackground: (product) => !!product.row_label,
      columnSizes: { sm: '24px' },
      getHeaderClassNames: () => '!p-0',
      getCellClassNames: (product) =>
        classNames(
          '!p-0 overflow-hidden',
          product.row_label && [
            '!min-h-21',
            ROW_LABEL_RAIL_CLASSES[product.row_label]?.cell,
          ],
        ),
      getCellDivClassNames: () => 'justify-center',
      render: (product) =>
        product.row_label ? (
          <RowLabelRail
            label={product.row_label}
            text={tr(
              `row_label_${product.row_label.toLowerCase()}` as keyof StaticText,
              this.staticText,
            )}
            lang={this.lang}
          />
        ) : null,
    };
  }

  protected CheckFillSize(resultColumns: ComparisonTableColumn[]) {
    const sizes: ('sm' | 'md' | 'lg')[] = ['sm', 'md', 'lg'];
    sizes.forEach((size) => {
      const hasFill = resultColumns.some((c) =>
        c.columnSizes?.[size]?.includes('1fr'),
      );
      if (hasFill) {
        return;
      }

      for (let idx = resultColumns.length - 1; idx >= 0; idx--) {
        const resultColumn = resultColumns[idx];
        if (resultColumn.field === 'call_to_action') {
          continue;
        }

        // Per CMS-1594: Fallback to the rightmost data column when this breakpoint
        // has no existing fill (`1fr`) column.
        // Large screens: allow that column to flex from `auto` up to `1fr`.
        // Small/Medium screens: preserve the configured minimum width while allowing fill.
        resultColumn.columnSizes![size] =
          size === 'lg'
            ? `minmax(auto, 1fr)`
            : `minmax(${resultColumn.columnSizes![size]}, 1fr)`;
        break;
      }
    });
  }

  public GetColumns(
    attributeList: Partial<
      Record<
        ColumnType,
        {
          header: string;
          size: ColumnSizeToken | undefined;
          style: AttributesListStyleEnum | undefined;
        }
      >
    >,
    baseColumns: BaseColumnComparisonTable[],
    calculationError: boolean,
    onRewardColumnClick: OnRewardColumnClickHandler,
    changedLoanSearchParams: LoanSearchParams | undefined,
    ctaSize: ColumnSizeToken,
  ): ComparisonTableColumn[] {
    const ctaBaseColumn = baseColumns.find(
      (x) => x.column.keyColumn === 'APPLY_NOW_BUTTON',
    );
    const desktopCtaColumn = this.getActionColumn(
      false,
      ctaBaseColumn?.columnSizing ?? ctaSize,
      baseColumns,
      changedLoanSearchParams,
    );

    const resultColumns = [
      ...this.getBaseColumns(
        onRewardColumnClick,
        baseColumns,
        changedLoanSearchParams,
        ctaSize,
      ),
      ...this.getAttributesColumns(attributeList, calculationError),
      desktopCtaColumn,
    ].filter((c) => c) as ComparisonTableColumn[];

    if (resultColumns.length) {
      this.CheckFillSize(resultColumns);
    }

    return resultColumns;
  }

  public getBaseColumns(
    onRewardColumnClick: OnRewardColumnClickHandler,
    baseCTColumns: BaseColumnComparisonTable[],
    changedLoanSearchParams: LoanSearchParams | undefined,
    ctaSize: ColumnSizeToken,
  ): ComparisonTableColumn[] {
    const columns: BaseColumnComparisonTable[] = baseCTColumns.length
      ? baseCTColumns
      : this.defaultBaseColumns;

    return (
      columns
        .map<ComparisonTableColumn | undefined>((col) => {
          switch (col.column.keyColumn) {
            case 'BANK_LOGO':
              return {
                header: col.column.nameColumn,
                field: 'product_image',
                getHeaderClassNames: () =>
                  'col-span-1 border-r group-[.table-scrolled]:border-r-borders/default !xl:border-r-0 border-r-transparent',
                getCellClassNames: () =>
                  `${PROVIDER_LOGO_CELL} border-r group-[.table-scrolled]:border-r-borders/default !xl:border-r-0 border-r-transparent`,
                getCellDivClassNames: () => 'justify-center',
                columnSizes: col.columnSizing
                  ? {
                      sm: GetSize(col.columnSizing, 'sm'),
                      md: GetSize(col.columnSizing, 'md'),
                      lg: GetSize(col.columnSizing, 'lg'),
                    }
                  : {
                      sm: '64px',
                      md: '104px',
                    },
                style: col.style,
                render: (product, _, __, ___, ____, renderCell) => {
                  return renderCell(
                    <CmsImage
                      src={product?.product_image ?? ''}
                      alt={product?.product_name ?? 'Product'}
                      width={78}
                      height={56}
                      sizes={{ _: 50, md: 78 }}
                      className={classNames(
                        'max-h-provider/asset-cell-asset-card-height w-provider/asset-cell-asset-card-width max-w-provider/asset-cell-asset-card-width cursor-pointer object-contain',
                      )}
                    />,
                  );
                },
              };

            case 'PRODUCT_NAME':
              return {
                header: col.column.nameColumn,
                field: 'product_name',
                getCellDivContentClassNames: () => {
                  return 'line-clamp-none';
                },
                columnSizes: GetColumnSizes(
                  this.vertical,
                  'PRODUCT_NAME',
                  col.columnSizing,
                ),
                style: col.style,
                render: (product, _, __, ___, ____, renderCell) => {
                  return product?.product_name
                    ? renderCell(product?.product_name)
                    : null;
                },
              };

            case 'APPLY_NOW_BUTTON':
              return this.getActionColumn(
                true,
                col.columnSizing ?? ctaSize,
                baseCTColumns,
                changedLoanSearchParams,
              )!;

            case 'HOT_REWARD':
              return this.GetTrendingRewardColumn(
                onRewardColumnClick,
                baseCTColumns,
              )!;

            case 'REWARD_VALUE':
              return this.GetTotalRewardsColumn(
                onRewardColumnClick,
                baseCTColumns,
                changedLoanSearchParams,
              )!;

            case 'APPROVAL_ODDS':
              return this.GetApprovalOddsColumn(baseCTColumns)!;
          }
        })
        // The switch is exhaustive over `BaseColumnKey`, but the key reaches it from the CMS through
        // an unchecked cast in the mapper, so one the frontend does not handle - Google Ratings,
        // whose Strapi enum value outlives its removal here - falls through as `undefined`. Dropped
        // here rather than at each read: the PL default model anchors its interleave on
        // `baseColumns[length - 1]`, which a hole would silently shift.
        .filter((column): column is ComparisonTableColumn => Boolean(column))
    );
  }

  /**
   * A string return is a real value; the empty states come back as `<NA>`, so a caller can put a
   * fixed label in front of a value without also putting it in front of the dash.
   */
  protected resolveAttributeCellValue(
    items: BaseAttribute[] | null | undefined,
    name: string,
    withPrefix = true,
  ): ReactNode {
    if (!items) return <NA text="-" />;
    const item = items.find((item) => getAttributeName(item) === name);
    if (!item) {
      return <NA text="-" />;
    }

    const value = formatAttributeItemValue(item, false, true, withPrefix);
    // `formatAttributeItemValue` spells a missing number as " - ".
    if (!value || value.trim() === '-') {
      return <NA text="-" />;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isEmptyText = value === (item as any).empty_value_text;
    return isEmptyText ? (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      <NA text={(item as any).empty_value_text} isBlackColor />
    ) : (
      value
    );
  }

  protected findByTypeAndConcatenate(
    calculateError: boolean,
    items: BaseAttribute[] | null | undefined,
    name: string,
    renderedHelp: ReactNode | null,
    renderCell: (children: ReactNode) => ReactNode | null,
  ) {
    if (calculateError) {
      return (
        <NA text={tr('calculateErrorPlaceholderMessage', this.staticText)} />
      );
    }
    if (!items) return <NA text="-" />;
    const item = items.find((item) => getAttributeName(item) === name);
    if (!item) {
      return <NA text="-" />;
    }

    const value = formatAttributeItemValue(item, false, true);
    if (!value) {
      return <NA text="-" />;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isEmptyText = value === (item as any).empty_value_text;
    const returnValue = isEmptyText ? (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      <NA text={(item as any).empty_value_text} isBlackColor />
    ) : (
      value
    );

    const wrappedHelp = renderedHelp ? <> {renderedHelp}</> : null;

    if (name === 'CARD_BENEFIT' && typeof returnValue === 'string') {
      if (returnValue.includes('%')) {
        const [val, val2] = returnValue.split('%');
        return renderCell(
          <p>
            {val}%<br />
            {val2}
            {wrappedHelp}
          </p>,
        );
      }
      if (returnValue.includes('/')) {
        const [val, val2] = returnValue.split('/');
        const val1WithoutSpaces = val.trim().split(' ');
        const lastElementFromArray = val1WithoutSpaces.pop();

        return renderCell(
          <p>
            {val1WithoutSpaces.join(' ')}
            <br />
            {lastElementFromArray} /{val2}
            {wrappedHelp}
          </p>,
        );
      }
    }

    return typeof returnValue === 'string'
      ? renderCell(
          <span>
            {returnValue}
            {wrappedHelp}
          </span>,
        )
      : renderCell(returnValue);
  }

  protected getAdditionalInformation = (
    items: BaseAttribute[],
    name: string,
  ) => {
    const item = items.find((item) => getAttributeName(item) === name);
    if (!item) {
      return null;
    }
    switch (item.__typename) {
      case 'ComponentAttributesAttrNumberRange':
      case 'ComponentAttributesAttrNumber': {
        return item.additional_information ?? null;
      }
      default:
        return null;
    }
  };

  protected getAttributesColumns(
    attribute_list: Partial<
      Record<
        ColumnType,
        {
          header: string;
          size: ColumnSizeToken | undefined;
          style: AttributesListStyleEnum | undefined;
        }
      >
    >,
    calculateError: boolean,
  ): ComparisonTableColumn[] {
    const attributesColumns: ComparisonTableColumn[] = Object.entries(
      attribute_list,
    ).map<ComparisonTableColumn>(([key, value], index) => {
      // GOOGLE_RATINGS is a key-value attribute (rate_score + rate_count) that also needs the
      // sibling GOOGLE_LINK attribute and a star widget. The generic cell below reads a key-value
      // attribute as `values[0].value` of its first key, so it would print just the score - hence
      // its own renderer, the way CARD_BENEFIT and MONTHLY_REPAYMENT are special-cased below.
      if (key === 'GOOGLE_RATINGS') {
        return this.GetRatingsColumn({
          header: value.header,
          size: value.size,
          style: value.style,
        });
      }

      return {
        header: value.header,
        columnSizes: GetColumnSizes(
          this.vertical,
          key as ColumnType,
          value.size,
        ),
        style: value.style,
        getCellDivContentClassNames: () => {
          switch (key) {
            case 'MONTHLY_REPAYMENT': {
              if (this.vertical === 'PERSONAL_LOANS' && index === 0) {
                return '!text-brand-primary-dark !font-font-weight/table-cell-strong';
              }
              return '';
            }
            case 'CARD_BENEFIT':
              return 'font-font-weight/table-cell-strong';
            case 'ANNUAL_FEE':
              return '!text-[--text-secondary-lighter]';
            default:
              return '';
          }
        },
        field: key as any,
        render: (product, _, __, ___, renderedHelp, renderCell) => {
          return this.findByTypeAndConcatenate(
            calculateError,
            product?.attributes ?? [],
            key,
            renderedHelp,
            renderCell,
          );
        },
        renderHelp: (product) => {
          return this.getAdditionalInformation(product?.attributes ?? [], key);
        },
      };
    });
    return attributesColumns;
  }

  public getActionColumn = (
    isMobile: boolean,
    size: ColumnSizeToken,
    baseCTColumns: BaseColumnComparisonTable[],
    changedLoanSearchParams?: LoanSearchParams,
  ): ComparisonTableColumn | undefined => {
    if (
      baseCTColumns.length &&
      !baseCTColumns.find((x) => x.column.keyColumn === 'APPLY_NOW_BUTTON')
    ) {
      return undefined;
    }

    const baseColumn = baseCTColumns?.find(
      (x) => x.column.keyColumn === 'APPLY_NOW_BUTTON',
    );

    return {
      header: '',
      field: 'call_to_action',
      getCellClassNames: () =>
        classNames({
          'justify-center lg:!p-0': isMobile,
          'justify-items-end hidden lg:block': !isMobile,
        }),
      getHeaderClassNames: () =>
        classNames({
          'justify-center lg:!p-0': isMobile,
          'justify-items-end hidden lg:block': !isMobile,
        }),
      // `!line-clamp-none` because `renderCell` clamps to two lines, which a
      // button stacked over a note exceeds on its own.
      getCellDivContentClassNames: () => 'w-full !line-clamp-none',
      getCellDivClassNames: () => 'w-full',
      columnSizes: {
        sm: isMobile ? GetSize(size, 'sm') : '0',
        md: isMobile ? GetSize(size, 'md') : '0',
        lg: isMobile ? '0' : GetSize(size, 'lg'),
      },
      style: baseColumn?.style,
      render: (product, _, rowIndex, ___, ____, renderCell) => {
        // <ApplyCtaLink> self-hides when the CTA url does not resolve (TI / GE /
        // TI-article products synthesize a url from reward_id / deeplink config
        // / url_alias; products with no destination render nothing), so no
        // visibility gate is needed here.
        const verdict = this.verdictFor?.(product as FilterableProduct);
        // UC 2: the table carries the reason a loan falls short and nothing
        // else. "You meet the basic criteria" is the product card's, and a
        // column of confirmations beside every Apply button is what the
        // requirement deliberately keeps off the table.
        const shortfall = isPlayedDown(verdict) ? verdict : undefined;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const onClick: MouseEventHandler<HTMLButtonElement> = (e) => {
          e.stopPropagation();
          sessionAttributionTracker.recordApplyClick();
          sendProductAnalytics({
            action: UserActionEnum.CLICK,
            componentTitle: `Product List/Button_Apply`,
            componentType: ComponentTypeEnum.TABLE,
            event: 'product_click_apply',
            index: rowIndex.toString(),
            product: product,
            url: this.slug,
            pushDataLayer: this.pushDataLayer,
            approvalDegree: product.title
              ? this.approvalOddsData?.odds[product.title]
              : undefined,
            approvalDegreeState: this.approvalOddsData?.userState,
          });
        };

        return renderCell(
          <div
            className={classNames('w-full', {
              'block lg:hidden': isMobile,
              'hidden lg:block': !isMobile,
            })}
          >
            <ApplyCtaLink
              product={product}
              ctaLinkOverride={product?.override_cta_link}
              loanParams={changedLoanSearchParams}
              e2eCTA={product.e2eToogleButton}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                size="small"
                title={product?.call_to_action?.links?.[0]?.label}
                styleType="cta"
                variant="outline"
                className={classNames(
                  'w-full group-hover/row:border-brand-cta group-hover/row:bg-brand-cta group-hover/row:text-white',
                )}
                onClick={onClick}
              >
                {product?.override_cta_label ?? this.staticText.apply}
              </Button>
            </ApplyCtaLink>
            <EligibilityTableMarker
              verdict={shortfall}
              className="mt-1 block text-left"
            />
          </div>,
        );
      },
    };
  };

  protected GetTotalRewardsColumn(
    onRewardColumnClick: OnRewardColumnClickHandler,
    baseCTColumns: BaseColumnComparisonTable[],
    changedLoanSearchParams: LoanSearchParams | undefined,
  ): ComparisonTableColumn | undefined {
    const column = baseCTColumns.find(
      (x) => x.column.keyColumn === 'REWARD_VALUE',
    );

    if (!column && baseCTColumns.length && this.vertical !== 'PERSONAL_LOANS') {
      return undefined;
    }

    let title = column?.column.nameColumn;

    if (!title) {
      title =
        this.vertical === 'INVESTMENTS' || this.vertical === 'BANKING'
          ? this.staticText.brand_reward_stk_bka
          : this.staticText.brand_reward;
    }

    const columnTitle = title;
    const getRewardCell = (product: CmsProduct) =>
      this.getTotalRewardCell(product, changedLoanSearchParams);

    return {
      header: title,
      columnSizes: GetColumnSizes(
        this.vertical,
        'BRAND_REWARD',
        column?.columnSizing,
      ),
      style: column?.style ?? 'HIGHLIGHT',
      onColumnClick: (e, product, rowIndex) =>
        onRewardColumnClick(e, product, rowIndex, {
          columnTitle,
          clickText: getRewardCell(product)?.text ?? EMPTY_CELL_TEXT,
        }),
      render: (product, _, __, ___, ____, renderCell) => {
        const rewardCell = getRewardCell(product);
        if (!rewardCell) {
          return <NA text={EMPTY_CELL_TEXT} />;
        }

        return renderCell(
          <Tooltip
            placement="top"
            onOpenEvent="hover"
            tooltipClass="px-6"
            content={
              <ul>
                {rewardCell.descriptions.map((d) => (
                  <li
                    style={{ fontWeight: 'var(--font-weight-body-1)' }}
                    key={d}
                  >
                    {d}
                  </li>
                ))}
              </ul>
            }
            infoClass="size-4 md:size-[18px] lg:size-[18px]"
          >
            <div
              className={classNames(
                'font-font-weight/table-cell-strong text-fill/primary-dark text-font-size/table-cell leading-line-height/table-cell',
                'whitespace-nowrap',
                REWARD_CELL_LINK_CLASSES,
              )}
            >
              {rewardCell.text}
            </div>
          </Tooltip>,
        );
      },
    };
  }

  private getTotalRewardCell(
    product: CmsProduct,
    changedLoanSearchParams: LoanSearchParams | undefined,
  ): { text: string; descriptions: string[] } | undefined {
    const giftOffers =
      getHighersRewardExclusiveOffersAndWelcomeOffersGitsFromProduct(
        product,
        changedLoanSearchParams,
        this.vertical !== 'PERSONAL_LOANS',
      );
    if (!giftOffers || (!giftOffers?.prefix && !giftOffers?.price)) {
      return undefined;
    }

    const value =
      this.vertical === 'PERSONAL_LOANS'
        ? giftOffers.price?.toLocaleString(this.lang)
        : formatNumberWithSeparator({
            value: calculateRewardValue({
              exclusiveOffers: [
                ...(product.exclusive_offers ?? []),
                ...(product.bundle_exclusive_offers ?? []),
              ],
              welcomeOffers: product.welcome_offers ?? [],
            }),
            thousandsSeparator: ',',
          });

    return {
      text: `${currencyFormat(giftOffers.prefix)}${value}`,
      descriptions: giftOffers.descriptions,
    };
  }

  protected GetTrendingRewardColumn(
    onRewardColumnClick: OnRewardColumnClickHandler,
    baseCTColumns: BaseColumnComparisonTable[],
  ): ComparisonTableColumn | undefined {
    const column = baseCTColumns.find(
      (x) => x.column.keyColumn === 'HOT_REWARD',
    );

    if (!column && baseCTColumns.length && this.vertical !== 'PERSONAL_LOANS') {
      return undefined;
    }

    let title = column?.column.nameColumn;

    if (!title) {
      title =
        this.vertical === 'PERSONAL_LOANS'
          ? this.staticText.trending_reward_pick
          : this.staticText.hot_reward_pick;
    }

    const columnTitle =
      this.vertical === 'PERSONAL_LOANS' ? title : HOT_REWARD_TRACKING_TITLE;
    const trendingRewardColumn: ComparisonTableBuilderColumn<CmsProduct> = {
      header: title,
      columnSizes: GetColumnSizes(
        this.vertical,
        this.vertical === 'PERSONAL_LOANS'
          ? 'TRENDING_REWARD_PICK'
          : 'HOT_REWARD_PICK',
        column?.columnSizing,
      ),
      style: column?.style,
      onColumnClick: (e, product, rowIndex) =>
        onRewardColumnClick(e, product, rowIndex, {
          columnTitle,
          clickText: getHotRewardDisplayText(product) || EMPTY_CELL_TEXT,
        }),
      render: (product, _, __, ___, ____, renderCell) => {
        const gift_title = getHotRewardDisplayText(product);

        if (!gift_title) {
          return <NA text={EMPTY_CELL_TEXT} />;
        }

        return renderCell(
          <FeLink
            href={createGoToLink(
              product!.url_alias!,
              SingSaverRewardsSectionId,
              this.vertical,
            )}
            className={REWARD_CELL_LINK_CLASSES}
          >
            {gift_title}
          </FeLink>,
        );
      },
    };

    return trendingRewardColumn;
  }

  protected GetApprovalOddsColumn(
    baseCTColumns: BaseColumnComparisonTable[],
  ): ComparisonTableColumn | undefined {
    const column = baseCTColumns.find(
      (x) => x.column.keyColumn === 'APPROVAL_ODDS',
    );

    if (!column && baseCTColumns.length) {
      return undefined;
    }

    const oddsData = this.approvalOddsData;
    const { isLocked, isFresh } = getApprovalOddsStatus(oddsData?.userState);
    const labels: ApprovalOddsLabels = {
      high: tr('approval_odds_high', this.staticText),
      medium: tr('approval_odds_medium', this.staticText),
      low: tr('approval_odds_low', this.staticText),
      unlock: tr('approval_odds_unlock', this.staticText),
    };
    const originalOnUnlockClick = this.onApprovalOddsUnlockClick;
    const originalOnClick = this.onApprovalOddsClick;

    const onUnlockClick = (product?: CmsProduct, rowIndex?: number) => {
      if (product) {
        sendProductAnalytics({
          action: UserActionEnum.CLICK,
          componentTitle: `Product List/Approval Odds Column_Unlock`,
          componentType: ComponentTypeEnum.TABLE,
          event: 'product_interaction',
          index: rowIndex?.toString() ?? '',
          product: product,
          url: this.slug,
          pushDataLayer: this.pushDataLayer,
          approvalDegree: product.title
            ? this.approvalOddsData?.odds[product.title]
            : undefined,
          approvalDegreeState: this.approvalOddsData?.userState,
        });
      }

      if (originalOnUnlockClick) {
        originalOnUnlockClick();
      }
    };

    const onClick = (product?: CmsProduct, rowIndex?: number) => {
      if (product) {
        sendProductAnalytics({
          action: UserActionEnum.CLICK,
          componentTitle: isFresh
            ? `Product List/Approval Odds Column_Label Details`
            : `Product List/Approval Odds Column_Refresh Expired`,
          componentType: ComponentTypeEnum.TABLE,
          event: 'product_interaction',
          index: rowIndex?.toString() ?? '',
          product: product,
          url: this.slug,
          pushDataLayer: this.pushDataLayer,
          approvalDegree: product.title
            ? this.approvalOddsData?.odds[product.title]
            : undefined,
          approvalDegreeState: this.approvalOddsData?.userState,
        });
      }

      if (originalOnClick) {
        originalOnClick();
      }
    };

    return {
      header: column?.column.nameColumn || tr('approval_odds', this.staticText),
      field: undefined,
      columnSizes: GetColumnSizes(
        this.vertical,
        'APPROVAL_ODDS',
        column?.columnSizing,
      ),
      style: column?.style,
      headerInfo: tr('approval_odds_tooltip', this.staticText),
      headerInfoTrackingTitle: 'Product List/Approval Odds Column_Tooltip',
      onHeaderInfoClick: isLocked ? onUnlockClick : onClick,
      render: (product, _, rowIndex, ___, ____, renderCell) => {
        const degree = product.title
          ? oddsData?.odds[product.title]
          : undefined;

        return renderCell(
          <ApprovalOddsBadge
            degree={degree}
            isLocked={isLocked}
            isFresh={isFresh}
            labels={labels}
            onUnlockClick={() => onUnlockClick(product, rowIndex)}
            onClick={() => onClick(product, rowIndex)}
          />,
        );
      },
    };
  }

  /**
   * `overrides` carry the editor's header, sizing and style when the column was picked in
   * `attributes_list` (CMS-1886). The legacy PL list passes nothing and applies its own overrides
   * afterwards. The header falls back to the built-in localized copy, so an Attributes entry left
   * unlabelled does not print a code-looking string as a public column header.
   */
  protected GetRatingsColumn(overrides?: {
    header?: string;
    size?: ColumnSizeToken;
    style?: AttributesListStyleEnum;
  }): ComparisonTableColumn {
    return {
      header: overrides?.header || tr('ratings_column_header', this.staticText),
      field: undefined,
      columnSizes: GetColumnSizes(
        this.vertical,
        'GOOGLE_RATINGS',
        overrides?.size,
      ),
      style: overrides?.style,
      onColumnClick: (e) => {
        e.stopPropagation();
      },
      render: (product, _, rowIndex, ___, ____, renderCell) => {
        const reviewData = getGoogleReviewData(product);
        const googleLink = getGoogleLink(product);

        if (!reviewData || !googleLink) {
          return renderCell(<NA text="-" />);
        }

        return renderCell(
          <GoogleRating
            rateScore={reviewData.rateScore}
            rateCount={reviewData.rateCount}
            productId={product.uid}
            labelSize="medium"
            onClick={() =>
              sendProductAnalytics({
                action: UserActionEnum.CLICK,
                componentTitle: 'Product List/Review Module_See Review',
                componentType: ComponentTypeEnum.TABLE,
                event: 'product_interaction',
                index: rowIndex.toString(),
                product,
                url: this.slug,
                pushDataLayer: this.pushDataLayer,
                approvalDegree: product.title
                  ? this.approvalOddsData?.odds[product.title]
                  : undefined,
                approvalDegreeState: this.approvalOddsData?.userState,
                ratingContext: getGoogleRatingContext(reviewData),
              })
            }
          />,
        );
      },
    };
  }
}

class PLColumnSource extends BaseColumnSource {
  protected readonly defaultBaseColumns: BaseColumnComparisonTable[] = [
    {
      column: {
        keyColumn: 'BANK_LOGO',
        nameColumn: getCardTitle(this.vertical, this.staticText),
      },
      columnSizing: undefined,
      style: undefined,
    },
    {
      column: {
        keyColumn: 'PRODUCT_NAME',
        nameColumn: '',
      },
      columnSizing: undefined,
      style: undefined,
    },
    {
      column: {
        keyColumn: 'APPLY_NOW_BUTTON',
        nameColumn: '',
      },
      columnSizing: undefined,
      style: undefined,
    },
    {
      column: {
        keyColumn: 'REWARD_VALUE',
        nameColumn: '',
      },
      columnSizing: undefined,
      style: undefined,
    },
  ];

  constructor(
    staticText: StaticText,
    vertical: VerticalType,
    pageType: PageType | undefined,
    slug: string,
    private readonly personalLoanOrder: PersonalLoanOrder | undefined,
    /**
     * `attributes_list` as configured on the table, before tab filtering. Both decides which column
     * model applies and supplies the rendered attribute columns (CMS-1886): `GetAttributeList`
     * deliberately ignores its own tab-filtered argument and reads this instead, so a PL table keeps
     * its columns when a tab is active. See the comment there for why.
     */
    private readonly configuredAttributes: ComponentAttribute[] | undefined,
    pushDataLayer: PushDataLayerFn,
    context: CmsClientContextValue,
    approvalOddsData?: ApprovalOddsData,
    onApprovalOddsUnlockClick?: () => void,
    onApprovalOddsClick?: () => void,
    verdictFor?: (product: FilterableProduct) => EligibilityVerdict,
  ) {
    super(
      staticText,
      vertical,
      pageType,
      slug,
      pushDataLayer,
      context,
      approvalOddsData,
      onApprovalOddsUnlockClick,
      onApprovalOddsClick,
      verdictFor,
    );
  }

  public override GetColumns(
    attributeList: Partial<
      Record<
        ColumnType,
        {
          header: string;
          size: ColumnSizeToken | undefined;
          style: AttributesListStyleEnum | undefined;
        }
      >
    >,
    baseCTColumns: BaseColumnComparisonTable[],
    calculationError: boolean,
    onRewardColumnClick: OnRewardColumnClickHandler,
    changedLoanSearchParams: LoanSearchParams,
    ctaSize: ColumnSizeToken,
  ): ComparisonTableColumn[] {
    // CMS-1886: once the editor has configured `attributes_list`, PL builds its columns the way every
    // other vertical does - base columns in editor order, then attributes, then Apply last. Going
    // through the base source is what makes that true, including the mobile/desktop Apply split.
    // Hidden base columns are already gone by this point: the mapper drops `visible === false`.
    if (this.usesSelfServeAttributes()) {
      return super.GetColumns(
        attributeList,
        baseCTColumns,
        calculationError,
        onRewardColumnClick,
        changedLoanSearchParams,
        ctaSize,
      );
    }

    const attributesColumns = this.getAttributesColumns(
      attributeList,
      calculationError,
    );
    const baseColumns = this.getBaseColumns(
      onRewardColumnClick,
      baseCTColumns,
      changedLoanSearchParams,
      ctaSize,
    );

    if (!this.personalLoanOrder?.item?.length) {
      const defaultColumnTypes = GetDefaultColumnTypes(
        this.brand,
        this.vertical,
      );
      const defaultPLAttributes = attributesColumns.filter((x) =>
        defaultColumnTypes.includes(x.field as ColumnType),
      );
      const [firstAttributeColumn, ...restAttributeColumns] =
        defaultPLAttributes;

      const baseColumnsExceptLast = baseColumns.slice(0, -1);

      const res = [
        ...baseColumnsExceptLast,
        firstAttributeColumn,
        baseColumns[baseColumns.length - 1],
        ...restAttributeColumns,
        this.getActionColumn(
          false,
          ctaSize,
          baseCTColumns,
          changedLoanSearchParams,
        ),
      ];

      return res.filter((c) => !!c);
    }

    const desktopApplyButton = this.getActionColumn(
      false,
      ctaSize,
      baseCTColumns,
      changedLoanSearchParams,
    );
    const mobileApplyButton = this.getActionColumn(
      true,
      ctaSize,
      baseCTColumns,
      changedLoanSearchParams,
    );

    const columnsToShow: ComparisonTableColumn[] = [];
    this.personalLoanOrder.item.forEach((item) => {
      let columnToAdd: ComparisonTableColumn | undefined = undefined;
      if (!item?.column || !item.visible) {
        return;
      }

      switch (item.column) {
        case 'BANK_LOGO': {
          if (
            baseCTColumns.length &&
            !baseCTColumns.find((x) => x.column.keyColumn === 'BANK_LOGO')
          ) {
            return;
          }

          columnToAdd = baseColumns.find((c) => c.field === 'product_image');
          break;
        }
        case 'PRODUCT_NAME': {
          if (
            baseCTColumns.length &&
            !baseCTColumns.find((x) => x.column.keyColumn === 'PRODUCT_NAME')
          ) {
            return;
          }

          columnToAdd = baseColumns.find((c) => c.field === 'product_name');
          break;
        }

        case 'APPLY_BUTTON': {
          if (
            baseCTColumns.length &&
            !baseCTColumns.find(
              (x) => x.column.keyColumn === 'APPLY_NOW_BUTTON',
            )
          ) {
            return;
          }

          columnToAdd = mobileApplyButton;
          break;
        }

        case 'HOTTEST_REWARD': {
          if (
            baseCTColumns.length &&
            !baseCTColumns.find((x) => x.column.keyColumn === 'HOT_REWARD')
          ) {
            return;
          }

          columnToAdd = this.GetTrendingRewardColumn(
            onRewardColumnClick,
            baseCTColumns,
          );

          break;
        }

        case 'REWARD_VALUE': {
          if (
            baseCTColumns.length &&
            !baseCTColumns.find((x) => x.column.keyColumn === 'REWARD_VALUE')
          ) {
            return;
          }

          columnToAdd = this.GetTotalRewardsColumn(
            onRewardColumnClick,
            baseCTColumns,
            changedLoanSearchParams,
          );
          break;
        }

        case 'ANNUAL_INTEREST_RATE': {
          columnToAdd = attributesColumns.find(
            (aC) => (aC.field as any) === 'ANNUAL_RATE',
          );
          break;
        }

        case 'EFFECTIVE_INTEREST_RATE': {
          columnToAdd = attributesColumns.find(
            (aC) => (aC.field as any) === 'APR',
          );
          break;
        }

        case 'INTEREST_RATE': {
          columnToAdd = this.GetInterestRateColumn(calculationError, undefined);
          break;
        }

        case 'TOTAL_INTEREST_PAYABLE': {
          columnToAdd = attributesColumns.find(
            (aC) => (aC.field as any) === 'TOTAL_INTEREST',
          );
          break;
        }

        case 'MONTHLY_REPAYMENT': {
          columnToAdd = attributesColumns.find(
            (aC) => (aC.field as any) === 'MONTHLY_REPAYMENT',
          );
          break;
        }

        case 'HANDLING_FEE': {
          columnToAdd = attributesColumns.find(
            (aC) => (aC.field as any) === 'HANDLING_FEE',
          );
          break;
        }

        case 'INTRODUCTORY_ANNUAL_RATE': {
          columnToAdd = attributesColumns.find(
            (aC) => (aC.field as any) === 'INTRODUCTORY_ANNUAL_RATE',
          );
          break;
        }

        case 'MAXIMUM_LOAN_AMOUNT': {
          columnToAdd = attributesColumns.find(
            (aC) => (aC.field as any) === 'MAXIMUM_LOAN_AMOUNT',
          );
          break;
        }

        case 'INTRODUCTORY_MONTHLY_REPAYMENT': {
          columnToAdd = attributesColumns.find(
            (aC) => (aC.field as any) === 'INTRODUCTORY_MONTHLY_REPAYMENT',
          );
          break;
        }

        case 'APPROVAL_ODDS': {
          if (
            baseCTColumns.length &&
            !baseCTColumns.find((x) => x.column.keyColumn === 'APPROVAL_ODDS')
          ) {
            return;
          }

          columnToAdd = this.GetApprovalOddsColumn(baseCTColumns);
          break;
        }

        case 'GOOGLE_RATINGS': {
          columnToAdd = this.GetRatingsColumn();
          break;
        }

        case 'WHY_APPLY_NOW': {
          columnToAdd = attributesColumns.find(
            (aC) => (aC.field as any) === 'WHY_APPLY_NOW',
          );
          break;
        }
      }

      if (!columnToAdd) {
        return;
      }

      if (item.columnSizing) {
        columnToAdd.columnSizes = GetColumnSizes(
          this.vertical,
          item.column as ColumnType,
          item.columnSizing,
        );

        if (item.column === 'APPLY_BUTTON') {
          columnToAdd.columnSizes.lg = '0';
        }

        if (desktopApplyButton?.columnSizes && item.column === 'APPLY_BUTTON') {
          desktopApplyButton.columnSizes.lg = GetColumnSizeByKey(
            this.vertical,
            item.column as ColumnType,
            'lg',
            item.columnSizing,
          );
        }
      }

      if (item.style) {
        columnToAdd.style = item.style;
      }

      columnsToShow.push(columnToAdd);
    });

    const resultColumns = columnsToShow.filter((c) => !!c);

    // If there is no fill column, make last one column width as auto to compensate table w-full
    if (resultColumns.length) {
      this.CheckFillSize(resultColumns);
    }

    if (desktopApplyButton) {
      resultColumns.push(desktopApplyButton);
    }

    return resultColumns;
  }

  /**
   * The Attributes list names the composite column by the `INTEREST_RATE` attribute, but its cell is
   * built from ANNUAL_RATE and APR, so the generic attribute column is swapped for it (CMS-2012).
   */
  protected override getAttributesColumns(
    attributeList: AttributeListRecord,
    calculateError: boolean,
  ): ComparisonTableColumn[] {
    return super
      .getAttributesColumns(attributeList, calculateError)
      .flatMap((column) => {
        if ((column.field as string) !== 'INTEREST_RATE') {
          return [column];
        }
        const interestRate = this.GetInterestRateColumn(
          calculateError,
          attributeList.INTEREST_RATE,
        );
        return interestRate ? [interestRate] : [];
      });
  }

  /**
   * `undefined` outside the brands in `INTEREST_RATE_COLUMN_BY_BRAND`, on both configuration paths.
   * `configured` is the Attributes-list row; the legacy list sets its sizing on the returned column.
   */
  private GetInterestRateColumn(
    calculationError: boolean,
    configured: AttributeListRecord[ColumnType] | undefined,
  ): ComparisonTableColumn | undefined {
    if (!INTEREST_RATE_COLUMN_BY_BRAND[this.brand]) {
      return undefined;
    }

    return {
      header: configured?.header || this.staticText.interest_rate,
      field: 'INTEREST_RATE' as any,
      columnSizes: GetColumnSizes(
        this.vertical,
        'INTEREST_RATE',
        configured?.size,
      ),
      style: configured?.style,
      getCellDivContentClassNames: () => 'line-clamp-none',
      render: (product, _, __, ___, ____, renderCell) => {
        if (calculationError) {
          return (
            <NA
              text={tr('calculateErrorPlaceholderMessage', this.staticText)}
            />
          );
        }

        const attributes = product?.attributes ?? [];
        // The "From" and "EIR" labels are fixed by the column (CMS-2041), so the attributes' own
        // Strapi prefixes are dropped - otherwise content decides whether "From" shows at all, and
        // an APR prefixed "From" reads "EIR From 1.75 % p.a.". Suffixes stay editor-owned.
        const annualRate = this.resolveAttributeCellValue(
          attributes,
          'ANNUAL_RATE',
          false,
        );
        return renderCell(
          <>
            <div className="text-font-size/table-cell-primary font-font-weight/table-cell-primary leading-line-height/table-cell-primary text-text/primary">
              {typeof annualRate === 'string'
                ? `${this.staticText.interest_rate_from} ${annualRate}`
                : annualRate}
            </div>
            <div className="font-font-weight/table-cell-normal text-text/secondary-lighter">
              {this.staticText.eir}{' '}
              {this.resolveAttributeCellValue(attributes, 'APR', false)}
            </div>
          </>,
        );
      },
    };
  }

  /**
   * Deliberately ignores the `attributes` argument, which the block has already narrowed to the
   * active tab. A PL table's column set is page-level configuration, not per-tab: the derived
   * catalogue was never tab-filtered either, so filtering the self-serve list would be a behaviour
   * change, and the tab filter is currently a no-op that empties the list outright (it compares an
   * attribute name string against an array of `SystemAttribute` objects, so it never matches). Tab
   * metadata still reaches the headers through `getLabel`.
   */
  public override GetAttributeList(
    _: ComponentAttribute[],
    products: ComparisonTableProduct[],
  ): AttributeListRecord {
    if (this.usesSelfServeAttributes()) {
      return this.GetSelfServeAttributeList(
        this.configuredAttributes ?? [],
        products,
      );
    }

    return this.GetDerivedAttributeList(products);
  }

  /**
   * True when the table is configured with the standard controls rather than the legacy PL list.
   * The legacy list wins while it has items, mirroring the server-side rule in `cms` that stops an
   * editor from filling in both (CMS-1886).
   */
  private usesSelfServeAttributes(): boolean {
    if (this.personalLoanOrder?.item?.length) {
      return false;
    }

    // A row with no attribute picked is a savable state - the relation is not required, and the
    // server rule deliberately ignores blank rows so a stray "Add an entry" click cannot block a
    // save. Counting one here would switch the table to the standard model with nothing to show
    // and silently drop the whole derived catalogue, so only rows that resolve an attribute count.
    return Boolean(
      this.configuredAttributes?.some((entry) => entry?.attribute?.name),
    );
  }

  /**
   * Header defaults for the PL attribute columns that used to be named by the `personal_loan_order`
   * enum. Shared by both column models so a column keeps today's public copy when the editor leaves
   * the Attributes-collection label empty. Key order is the render order of the derived fallback and
   * `GetDefaultColumnTypes` depends on `MONTHLY_REPAYMENT` coming first - do not reorder.
   */
  private plDefaultHeaders(): Partial<Record<ColumnType, string>> {
    return {
      MONTHLY_REPAYMENT: this.staticText.monthly_repayment,
      ANNUAL_RATE: this.staticText.annual_interest_rate,
      TOTAL_INTEREST: this.staticText.total_cost_of_loan,
      APR: this.staticText.eir,
      HANDLING_FEE: this.staticText.handling_fee,
      INTRODUCTORY_ANNUAL_RATE: this.staticText.INTRODUCTORY_ANNUAL_RATE,
      MAXIMUM_LOAN_AMOUNT: this.staticText.MAXIMUM_LOAN_AMOUNT,
      INTRODUCTORY_MONTHLY_REPAYMENT:
        this.staticText.INTRODUCTORY_MONTHLY_REPAYMENT,
      WHY_APPLY_NOW: this.staticText.why_apply_now,
    };
  }

  /**
   * The catalogue the legacy PL list resolves its attribute columns against, and the fallback used
   * when a PL table configures neither control. Values of most of these are injected by the loan
   * calculator, so they stay dev-owned.
   */
  private GetDerivedAttributeList(
    products: ComparisonTableProduct[],
  ): AttributeListRecord {
    return Object.entries(this.plDefaultHeaders()).reduce<AttributeListRecord>(
      (prev, [name, defaultHeader]) => ({
        ...prev,
        [name as ColumnType]: {
          header: getLabel(products, name, defaultHeader),
          size: undefined,
          style: undefined,
        },
      }),
      {},
    );
  }

  /**
   * Attribute columns picked from the Attributes collection (CMS-1886). Values are read from the
   * product generically by relation name, so a new attribute needs no code change - only the header
   * is resolved here.
   */
  private GetSelfServeAttributeList(
    attributes: ComponentAttribute[],
    products: ComparisonTableProduct[],
  ): AttributeListRecord {
    const defaultHeaders = this.plDefaultHeaders();

    return (attributes ?? [])
      .filter((_, index) => index < ATTRIBUTE_COLUMNS_MAX)
      .reduce<AttributeListRecord>((prev, current) => {
        const name = current?.attribute?.name;
        if (!name) {
          return prev;
        }

        return {
          ...prev,
          [name as ColumnType]: {
            // Per-table override first, then the tab / product-meta label, then the localized label
            // from the Attributes collection, then the copy this column shipped with. Never the raw
            // relation name: an attribute with no label would print `MINIMUM_LOAN_TENURE` as a
            // public column header. Interest rate takes the row label only: its collection entry is
            // the card one, and its default header is GetInterestRateColumn's.
            header:
              current.label ||
              (name === 'INTEREST_RATE'
                ? ''
                : getLabel(
                    products,
                    name,
                    current.attribute?.label ||
                      defaultHeaders[name as ColumnType] ||
                      '',
                  )),
            size: current.columnSizing,
            style: current.style,
          },
        };
      }, {});
  }
}

export const getColumnSourceByVertical = (
  vertical: VerticalType,
  staticText: StaticText,
  pageType: PageType | undefined,
  slug: string,
  personalLoanOrder: PersonalLoanOrder | undefined,
  configuredAttributes: ComponentAttribute[] | undefined,
  pushDataLayer: PushDataLayerFn,
  context: CmsClientContextValue,
  approvalOddsData?: ApprovalOddsData,
  onApprovalOddsUnlockClick?: () => void,
  onApprovalOddsClick?: () => void,
  verdictFor?: (product: FilterableProduct) => EligibilityVerdict,
): BaseColumnSource => {
  switch (vertical) {
    case 'PERSONAL_LOANS':
      return new PLColumnSource(
        staticText,
        vertical,
        pageType,
        slug,
        personalLoanOrder,
        configuredAttributes,
        pushDataLayer,
        context,
        approvalOddsData,
        onApprovalOddsUnlockClick,
        onApprovalOddsClick,
        verdictFor,
      );
    default:
      return new BaseColumnSource(
        staticText,
        vertical,
        pageType,
        slug,
        pushDataLayer,
        context,
        approvalOddsData,
        onApprovalOddsUnlockClick,
        onApprovalOddsClick,
        verdictFor,
      );
  }
};

// Width rules (by CTA label)
// CMS-1295
const hardcodedSizeTitles: Record<string, ColumnSizeToken> = {
  Apply: 'S',
  Buy: 'S',
  投保: 'S',
  立即申請: 'M',
  'Buy Now': 'M',
  立即投保: 'M',
  'Check Price': 'L',
};

const getCtaColumnSize = (title: string): ColumnSizeToken => {
  return hardcodedSizeTitles[title] ?? 'M';
};
